import type { LanguageDto } from "@core/data/dto/language.dto.ts";
import type { CurrentLanguageDto } from "@core/data/dto/update-current-language.dto.ts";
import type { IotApiClient } from "@core/data/infrastructures/iot-api-client/iot-api-client";
import { LanguageUpdateCurrentMapper } from "@core/data/mappers/language-update-current.mapper.ts";
import { LanguageMapper } from "@core/data/mappers/language.mapper.ts";
import { FallbackError } from "@core/domain/errors/fallback.error";
import type {
    Language,
    LanguageIsoCode,
} from "@core/domain/models/language.model";
import { LanguageIsoCodeEnum } from "@core/domain/models/language.model";
import { coreTypes } from "@core/root/core-types.di";
import { inject, injectable } from "inversify";
import type { ResultAsync } from "neverthrow";
import { err, ok } from "neverthrow";

@injectable()
export class LanguageRemoteDatasource {
    constructor(
        @inject(coreTypes.infrastructure.IotApiClient)
        private readonly iotApiClient: IotApiClient,
        @inject(LanguageMapper)
        private readonly languageMapper: LanguageMapper,
        @inject(LanguageUpdateCurrentMapper)
        private readonly updateCurrentLanguageMapper: LanguageUpdateCurrentMapper,
    ) {}

    fetchAll(): ResultAsync<Language[], FallbackError> {
        return this.iotApiClient.http
            .get<LanguageDto[]>("/users/languages/")
            .mapErr(() => new FallbackError())
            .map((languagesResponse) =>
                languagesResponse.data.mapNotNull((languageDto) =>
                    this.languageMapper.map(languageDto),
                ),
            );
    }

    update(
        language: LanguageIsoCodeEnum,
    ): ResultAsync<LanguageIsoCode, FallbackError> {
        const languageDto = this.updateCurrentLanguageMapper.mapToDto(language);

        return this.iotApiClient.http
            .put<CurrentLanguageDto>("/users/locale/", languageDto)
            .mapErr(() => new FallbackError())
            .andThen((response) => {
                const updatedLanguage = this.languageMapper.mapToIsoCode(
                    response.data,
                );

                if (!updatedLanguage) return err(new FallbackError());

                return ok(updatedLanguage);
            });
    }
}
